/**
 * KAN-348 review (KAN-271 follow-up): answer carry-over across the
 * authored-questions -> attempt-snapshot swap.
 *
 * When the async attempt snapshot replaces the initially-rendered authored
 * questions, a learner may already have clicked an answer. Carrying that
 * answer blindly BY INDEX silently corrupts it when the snapshot is
 * shuffled (KAN-350) or bank-drawn (KAN-347): index i now points at a
 * different option order or a different question entirely.
 *
 * These helpers carry an answer ONLY when the question at that index is
 * content-identical between the two arrays — same id/type/text and the
 * exact same option/row/column/matching arrays INCLUDING ORDER. Anything
 * else drops the answer (a null slot; the learner re-answers).
 */

function labelsEqual(a: any[] | undefined, b: any[] | undefined, key: string): boolean {
  const aArr = Array.isArray(a) ? a : [];
  const bArr = Array.isArray(b) ? b : [];
  if (aArr.length !== bArr.length) return false;
  for (let i = 0; i < aArr.length; i++) {
    const av = aArr[i]?.[key] ?? aArr[i];
    const bv = bArr[i]?.[key] ?? bArr[i];
    if (av !== bv) return false;
  }
  return true;
}

/**
 * True when two question objects present the SAME content in the SAME order —
 * i.e. an answer given against `a` means the same thing against `b`.
 */
export function isSameQuestionContent(a: any, b: any): boolean {
  if (!a || !b) return false;
  if (a.type !== b.type) return false;
  if ((a.text ?? '') !== (b.text ?? '')) return false;
  // When both carry ids they must match (a bank draw yields a different id).
  if (a.id != null && b.id != null && a.id !== b.id) return false;

  return (
    labelsEqual(a.options, b.options, 'text') &&
    labelsEqual(a.gridRows, b.gridRows, 'label') &&
    labelsEqual(a.gridColumns, b.gridColumns, 'label') &&
    labelsEqual(a.matchLeft, b.matchLeft, 'label') &&
    labelsEqual(a.matchRight, b.matchRight, 'label')
  );
}

/**
 * Build the userAnswers array for `nextQuestions`, carrying each prior answer
 * onto the content-identical question it was given for. The same index is
 * preferred; otherwise the answer follows its question to wherever it moved
 * (KAN-678: an instructor edit can re-freeze the attempt with a question
 * inserted ahead of answered ones). Each prior answer is used at most once;
 * every other slot is null.
 */
export function carryAnswersForIdenticalQuestions(
  priorAnswers: any[],
  priorQuestions: any[],
  nextQuestions: any[]
): any[] {
  const prior = Array.isArray(priorAnswers) ? priorAnswers : [];
  const oldQs = Array.isArray(priorQuestions) ? priorQuestions : [];
  const used = new Set<number>();
  const take = (j: number) => {
    used.add(j);
    return prior[j] ?? null;
  };
  return (nextQuestions || []).map((q, i) => {
    if (!used.has(i) && isSameQuestionContent(oldQs[i], q)) return take(i);
    const j = oldQs.findIndex((old, k) => !used.has(k) && k !== i && isSameQuestionContent(old, q));
    return j >= 0 ? take(j) : null;
  });
}

/** True when `a` holds an answer the learner actually entered — a non-null
 *  scalar or a NON-empty array (select-all / grid). Mirrors the per-slot test
 *  hasInProgressAnswers() uses in the component. */
function isLocallySet(a: any): boolean {
  return Array.isArray(a) ? a.length > 0 : (a !== null && a !== undefined);
}

/**
 * KAN-345 (resume-clobber): apply the server's autosaved answers onto the
 * current answers WITHOUT overwriting a slot the learner has already set
 * locally.
 *
 * On a genuine reload/resume nothing is set locally (every slot is null), so
 * each slot takes its saved value — resume-restore is preserved. But when a
 * learner clicked an answer in the ~1s window before a late/duplicate
 * attempt-start response echoed savedAnswers, that locally-set slot is kept
 * instead of being clobbered. Untouched slots are filled from savedAnswers;
 * missing slots pad to `questionCount` with null.
 */
export function mergeSavedAnswersPreservingLocal(
  localAnswers: any[],
  savedAnswers: any[],
  questionCount: number
): any[] {
  const local = Array.isArray(localAnswers) ? localAnswers : [];
  const saved = Array.isArray(savedAnswers) ? savedAnswers : [];
  return Array.from({ length: questionCount }, (_unused, i) =>
    isLocallySet(local[i]) ? local[i] : (saved[i] ?? null)
  );
}
